import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button } from '@/components/ui/Button';
import { EmptyState } from '@/components/ui/EmptyState';
import { Input, Textarea } from '@/components/ui/Input';
import { Lightbox } from '@/components/Lightbox';
import { ReportDialog } from '@/features/report/ReportDialog';
import { container } from '@/services/container';
import { useFeedStore } from '@/store/feed';
import { useUiStore } from '@/store/ui';
import { MeshLinkError } from '@/lib/result';
import { markTaskDone } from '@/utils/tasks';
import { CONTENT_MAX_IMAGES_PER_POST } from '@/config';
import type { Post, PostMedia } from '@shared/types';
import { sha256 } from '@noble/hashes/sha256';
import { bytesToHex } from '@noble/hashes/utils';

/** 字符串 → 64 位十六进制（搜索词哈希，明文不上链）。 */
function sha256Hex(text: string): string {
  return bytesToHex(sha256(new TextEncoder().encode(text)));
}

/** 媒体字节（视频/音频）→ 临时 objectURL（组件卸载时撤销）。传输未装配时静默降级。 */
function usePostMedia(cid: string | undefined): { url: string | null } {
  const [state, setState] = useState<{ url: string | null }>({ url: null });
  useEffect(() => {
    let active = true;
    let created: string | null = null;
    if (!cid || !container.hasPeerClient()) {
      setState({ url: null });
      return;
    }
    void container.feed
      .getImage(cid)
      .then((res) => {
        if (!active || !res) return;
        created = URL.createObjectURL(new Blob([new Uint8Array(res.bytes)], { type: res.mime }));
        setState({ url: created });
      })
      .catch(() => {
        if (active) setState({ url: null });
      });
    return () => {
      active = false;
      if (created) URL.revokeObjectURL(created);
    };
  }, [cid]);
  return state;
}

function PostThumb({
  cid,
  fullCid,
  onPreview,
}: {
  cid: string;
  /** 原图 cid（放大时加载，缺省退化为缩略图 cid） */
  fullCid?: string;
  onPreview?: (cid: string) => void;
}): JSX.Element {
  const url = usePostMedia(cid).url;
  if (!url) return <div className="h-24 w-24 rounded-btn bg-panel-2" aria-hidden />;
  const preview = (): void => onPreview?.(fullCid ?? cid);
  return (
    <img
      src={url}
      alt=""
      role="button"
      tabIndex={0}
      className="h-24 w-24 cursor-zoom-in rounded-btn object-cover transition hover:opacity-85"
      onClick={preview}
      onKeyDown={(e) => {
        if (e.key === 'Enter') preview();
      }}
    />
  );
}

/** 媒体附件渲染（视频/音频/直播）。 */
function PostMediaBlock({ media }: { media: PostMedia }): JSX.Element | null {
  const { url } = usePostMedia(media.cid);
  if (media.kind === 'live') {
    return (
      <div className="mt-3 flex items-center gap-2 rounded-btn border border-err/30 bg-err/5 px-3 py-2 text-xs text-err">
        <span className="inline-block h-2 w-2 animate-pulse rounded-full bg-err" aria-hidden />
        🔴 直播中
      </div>
    );
  }
  if (!url) return <div className="mt-3 rounded-btn bg-panel-2 px-3 py-4 text-center text-xs text-dim">媒体加载中…</div>;
  if (media.kind === 'audio') {
    return (
      <div className="mt-3 flex items-center gap-2 rounded-btn bg-panel-2 px-3 py-2">
        <span aria-hidden>🎙</span>
        <audio controls src={url} className="h-9 w-full max-w-xs" preload="none" />
        <span className="text-[11px] text-dim">{media.name || '语音'}</span>
      </div>
    );
  }
  return (
    <div className="mt-3">
      <video controls src={url} className="w-full rounded-btn bg-black" preload="metadata" />
      {media.name ? <p className="mt-1 text-[11px] text-dim">{media.name}</p> : null}
    </div>
  );
}

/** 链上社交动作统一入口（like/collect/subscribe/share/follow/unfollow）。 */
async function actSocial(
  action: 'like' | 'collect' | 'subscribe' | 'share',
  target: string,
): Promise<void> {
  const addr = container.identity.address;
  if (!addr) throw new MeshLinkError('E_LOCKED');
  await container.chain.socialAct({
    action,
    from: addr as `0x${string}`,
    target,
    signature: container.identity.signObject({ action, from: addr, target, ts: Date.now() }),
  });
}

/** 内容锚定（评论/转发/发布后存证），失败不阻断主流程。 */
async function anchorKind(kind: 'comment' | 'repost' | 'post' | 'image' | 'video' | 'audio' | 'live', cid: string): Promise<boolean> {
  const addr = container.identity.address;
  if (!addr || !cid) return false;
  try {
    await container.chain.anchor({
      author: addr as `0x${string}`,
      cid,
      signature: container.identity.signObject({ kind, cid, ts: Date.now() }),
      kind,
    });
    return true;
  } catch {
    return false;
  }
}

interface CommentItem {
  id: string;
  cid: string;
  author: string;
  text: string;
  ts: number;
}

function PostCard({
  post,
  onReport,
}: {
  post: Post;
  onReport: (id: string) => void;
}): JSX.Element {
  const liked = useFeedStore((s) => !!s.liked[post.id]);
  const [busy, setBusy] = useState<string | null>(null);
  /** 评论面板内联展开（点评论按钮即在本卡内显示，避免面板沉底不可见） */
  const [commentOpen, setCommentOpen] = useState(false);
  /** 点击放大的原图 cid（null 表示未预览） */
  const [previewCid, setPreviewCid] = useState<string | null>(null);
  const previewUrl = usePostMedia(previewCid ?? '').url;
  // 兜底：中继返回的历史数据可能缺字段，避免 `undefined.slice()` 把整页打崩
  const authorDid = typeof post.authorDid === 'string' ? post.authorDid : '';
  const manifestCid = typeof post.manifestCid === 'string' ? post.manifestCid : '';
  const images = Array.isArray(post.images) ? post.images : [];
  const media = post.media;
  const anchored = typeof post.blockHeight === 'number' && post.blockHeight > 0;

  const run = async (label: string, fn: () => Promise<void>): Promise<void> => {
    if (busy) return;
    setBusy(label);
    try {
      await fn();
    } catch (err) {
      useUiStore.getState().pushError(err instanceof MeshLinkError ? err.code : 'E_UNKNOWN');
    } finally {
      setBusy(null);
    }
  };

  const toggleLike = (): Promise<void> =>
    run('like', async () => {
      const cur = useFeedStore.getState().isLiked(post.id);
      if (!cur && manifestCid) await actSocial('like', manifestCid);
      useFeedStore.getState().setLiked(post.id, !cur);
    });

  const onCollect = (): Promise<void> =>
    run('collect', async () => {
      if (!manifestCid) throw new MeshLinkError('E_INVALID_INPUT');
      await actSocial('collect', manifestCid);
      useUiStore.getState().pushToast('success', '已收藏（上链存证）');
    });

  const onShare = (): Promise<void> =>
    run('share', async () => {
      if (!manifestCid) throw new MeshLinkError('E_INVALID_INPUT');
      await actSocial('share', manifestCid);
      if (typeof navigator.share === 'function') {
        await navigator.share({ title: post.authorName || 'MeshLink 内容', text: post.text || '', url: `https://mesh.doup.cn/feed#${manifestCid}` }).catch(() => undefined);
      } else {
        await navigator.clipboard?.writeText(`https://mesh.doup.cn/feed#${manifestCid}`).catch(() => undefined);
        useUiStore.getState().pushToast('success', '链接已复制（分享已上链）');
      }
    });

  const onRepost = (): Promise<void> =>
    run('repost', async () => {
      if (!manifestCid) throw new MeshLinkError('E_INVALID_INPUT');
      const ok = await anchorKind('repost', manifestCid);
      useUiStore.getState().pushToast('success', ok ? '已转发（repost 已上链）' : '已转发（锚定排队中）');
    });

  // —— 有效浏览埋点：可见 ≥10s 上报 browse（≥30s 视为完播 → browse_complete）——
  const viewRef = useRef<HTMLElement | null>(null);
  useEffect(() => {
    if (!anchored || !manifestCid) return;
    const el = viewRef.current;
    if (!el) return;
    const addr = container.identity.address;
    if (!addr) return;
    let startedAt = 0;
    let reported = false;
    const obs = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry.isIntersecting && !startedAt) {
          startedAt = Date.now();
        } else if (!entry.isIntersecting && startedAt > 0) {
          const dur = (Date.now() - startedAt) / 1000;
          if (!reported && dur >= 10) {
            reported = true;
            void container.chain
              .browse({
                from: addr as `0x${string}`,
                cid: manifestCid,
                duration: Math.min(600, Math.round(dur)),
                ratio: dur >= 30 ? 1 : 0.5,
              })
              .catch(() => undefined);
          }
          startedAt = 0;
        }
      },
      { threshold: 0.5 },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [anchored, manifestCid]);

  return (
    <article ref={viewRef as React.RefObject<HTMLElement>} className="card p-4">
      <header className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="truncate text-sm font-medium text-text">{post.authorName || '匿名用户'}</p>
          <p className="text-[11px] text-dim">
            {new Date(post.ts).toLocaleString()}
            {authorDid ? ` · ${authorDid.slice(0, 20)}…` : ''}
          </p>
        </div>
        <Button variant="ghost" size="sm" onClick={() => onReport(post.id)}>
          举报
        </Button>
      </header>

      {post.text ? <p className="mt-2 whitespace-pre-wrap text-sm text-text">{post.text}</p> : null}

      {images.length > 0 ? (
        <div className="mt-3 flex flex-wrap gap-2">
          {images.map((img) => (
            <PostThumb
              key={img.cid}
              cid={img.thumbCid || img.cid}
              fullCid={img.cid}
              onPreview={setPreviewCid}
            />
          ))}
        </div>
      ) : null}

      {media ? <PostMediaBlock media={media} /> : null}

      <footer className="mt-3 flex items-center gap-3">
        <button
          type="button"
          disabled={busy === 'like'}
          className="text-xs text-dim hover:text-accent disabled:opacity-50"
          onClick={() => void toggleLike()}
        >
          {liked ? '❤️ 已赞' : '♡ 赞'}
        </button>
        <button
          type="button"
          className="text-xs text-dim hover:text-accent"
          onClick={() => setCommentOpen((v) => !v)}
        >
          💬 评论
        </button>
        <button
          type="button"
          disabled={busy === 'repost'}
          className="text-xs text-dim hover:text-accent disabled:opacity-50"
          onClick={() => void onRepost()}
        >
          🔁 转发
        </button>
        <button
          type="button"
          disabled={busy === 'collect'}
          className="text-xs text-dim hover:text-accent disabled:opacity-50"
          onClick={() => void onCollect()}
        >
          ⭐ 收藏
        </button>
        <button
          type="button"
          disabled={busy === 'share'}
          className="text-xs text-dim hover:text-accent disabled:opacity-50"
          onClick={() => void onShare()}
        >
          ↗ 分享
        </button>
        {manifestCid ? <span className="mono text-[11px] text-dim">cid {manifestCid.slice(0, 12)}</span> : null}
        {post.blockHeight ? <span className="text-[11px] text-dim">#{post.blockHeight}</span> : null}
      </footer>

      {commentOpen ? <CommentPanel post={post} onClose={() => setCommentOpen(false)} /> : null}

      {previewUrl ? <Lightbox url={previewUrl} onClose={() => setPreviewCid(null)} /> : null}
    </article>
  );
}

/** 远端评论归一化（容错，防整页崩溃）。 */
function normalizeRemoteComment(raw: unknown): CommentItem | null {
  if (raw == null || typeof raw !== 'object') return null;
  const c = raw as Record<string, unknown>;
  const str = (v: unknown, fallback = ''): string => (typeof v === 'string' ? v : fallback);
  const num = (v: unknown, fallback: number): number => (typeof v === 'number' && Number.isFinite(v) ? v : fallback);
  const text = str(c.text).trim();
  if (!text) return null;
  return {
    id: str(c.id) || `c_${num(c.ts, Date.now())}`,
    cid: str(c.postCid) || str(c.cid),
    author: str(c.author) || (typeof c.authorPeer === 'string' ? c.authorPeer.slice(0, 10) : '匿名'),
    text,
    ts: num(c.ts, Date.now()),
  };
}

/** 评论面板：中继历史 + 实时订阅 + P2P 广播发送（每条评论上链存证 kind=comment）。 */
function CommentPanel({ post, onClose }: { post: Post; onClose: () => void }): JSX.Element {
  const cid = post.manifestCid;
  const [items, setItems] = useState<CommentItem[]>(() => {
    try {
      const raw = window.localStorage.getItem(`mesh_comments:${cid}`);
      return raw ? (JSON.parse(raw) as CommentItem[]) : [];
    } catch {
      return [];
    }
  });
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const itemsRef = useRef(items);
  itemsRef.current = items;

  const persistLocal = (key: string, list: CommentItem[]): void => {
    try {
      window.localStorage.setItem(`mesh_comments:${key}`, JSON.stringify(list.slice(-50)));
    } catch {
      /* 存储满则忽略 */
    }
  };

  const mergeComments = (base: CommentItem[], add: (CommentItem | null)[]): CommentItem[] => {
    const map = new Map<string, CommentItem>();
    for (const it of base) map.set(it.id, it);
    for (const it of add) if (it && !map.has(it.id)) map.set(it.id, it);
    return [...map.values()].sort((a, b) => a.ts - b.ts);
  };

  // 打开面板：拉中继历史 + 订阅实时；关闭时取消订阅
  useEffect(() => {
    if (!cid) return;
    let active = true;
    void container.feed
      .commentHistory(cid)
      .then((list) => {
        if (!active) return;
        const merged = mergeComments(itemsRef.current, list.map((raw) => normalizeRemoteComment(raw)));
        setItems(merged);
        persistLocal(cid, merged);
      })
      .catch(() => {
        /* 中继不可达：保留本地缓存 */
      });
    container.feed.commentSubscribe(cid, (comment) => {
      const item = normalizeRemoteComment(comment);
      if (!item) return;
      setItems((prev) => {
        if (prev.some((x) => x.id === item.id)) return prev;
        const next = [...prev, item];
        persistLocal(cid, next);
        return next;
      });
    });
    return () => {
      active = false;
      container.feed.commentUnsubscribe(cid);
    };
  }, [cid]);

  const send = async (): Promise<void> => {
    const trimmed = text.trim();
    if (!trimmed || !cid || sending) return;
    setSending(true);
    const item: CommentItem = {
      id: `c_${Date.now()}`,
      cid,
      author: container.identity.address?.slice(0, 10) || '匿名',
      text: trimmed,
      ts: Date.now(),
    };
    try {
      // 广播到中继（其他端实时可见 + 落盘持久化）
      await container.feed.commentPublish(cid, item);
      useUiStore.getState().pushToast('success', '评论已发送');
    } catch {
      // 中继不可达：不吞掉评论，降级提示但本地保留
      useUiStore.getState().pushToast('error', '中继暂不可用，评论仅保存在本机');
    }
    setItems((prev) => {
      if (prev.some((x) => x.id === item.id)) return prev;
      const next = [...prev, item];
      persistLocal(cid, next);
      return next;
    });
    setText('');
    // 锚定上链存证（解锁时；失败不阻断主流程）
    void anchorKind('comment', cid);
    setSending(false);
  };

  return (
    <div className="card mt-3 p-4">
      <div className="mb-2 flex items-center justify-between">
        <h3 className="text-sm font-medium text-text">评论 · {post.authorName || '匿名用户'}</h3>
        <Button variant="ghost" size="sm" onClick={onClose}>
          收起
        </Button>
      </div>
      {items.length === 0 ? (
        <p className="py-2 text-xs text-dim">还没有评论，来抢沙发（评论会上链存证、跨端可见）</p>
      ) : (
        <ul className="mb-2 space-y-2">
          {items.map((it) => (
            <li key={it.id} className="rounded-btn bg-panel-2 px-3 py-2 text-xs">
              <p className="font-medium text-text">{it.author}</p>
              <p className="mt-0.5 whitespace-pre-wrap text-dim">{it.text}</p>
              <p className="mt-1 text-[11px] text-dim">{new Date(it.ts).toLocaleString()}</p>
            </li>
          ))}
        </ul>
      )}
      <div className="flex gap-2">
        <Input
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') void send();
          }}
          placeholder="友善评论，评论即挖矿…"
        />
        <Button size="sm" loading={sending} onClick={() => void send()}>
          发送
        </Button>
      </div>
    </div>
  );
}

/** Feed 流：图文/视频/语音/直播发布 + 创作入口 + 社交动作（赞/评论/转发/收藏/分享）+ 搜索 + 有效浏览挖矿 + 广场历史。 */
export function FeedPage(): JSX.Element {
  const posts = useFeedStore((s) => s.posts);
  const [text, setText] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [publishing, setPublishing] = useState(false);
  const [reportTarget, setReportTarget] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [composer, setComposer] = useState<'video' | 'audio' | 'live' | null>(null);
  const [composerText, setComposerText] = useState('');
  const [mediaFile, setMediaFile] = useState<File | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const mediaRef = useRef<HTMLInputElement>(null);

  const load = useCallback((): void => {
    // 会话未装配（传输/内容存储未就绪）时静默跳过，避免噪声 Toast
    if (!container.hasPeerClient() || !container.hasContentStore()) return;
    void container.feed.loadHistory().catch((err) => {
      const code = err instanceof MeshLinkError ? err.code : 'E_UNKNOWN';
      useUiStore.getState().pushError(
        code,
        code === 'E_CONTENT_FETCH_TIMEOUT' ? { onAction: () => load() } : undefined,
      );
    });
  }, []);

  useEffect(() => {
    load();
    // 传输未装配（无身份会话）时静默跳过订阅，避免整页崩溃（T03 守卫）
    if (!container.hasPeerClient()) return;
    try {
      container.feed.subscribe(() => undefined);
    } catch {
      /* 未装配静默 */
    }
  }, [load]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return posts;
    return posts.filter((p) => (p.text || '').toLowerCase().includes(q) || (p.authorName || '').toLowerCase().includes(q));
  }, [posts, query]);

  const pickFiles = (list: FileList | null): void => {
    if (!list) return;
    const picked = Array.from(list).filter((f) => f.type.startsWith('image/'));
    if (picked.length > CONTENT_MAX_IMAGES_PER_POST) {
      useUiStore.getState().pushError('E_INVALID_INPUT');
      setFiles(picked.slice(0, CONTENT_MAX_IMAGES_PER_POST));
      return;
    }
    setFiles(picked);
  };

  /** 发布图文 + 自动锚定（kind=post/image），锚定失败不阻断发帖。 */
  const publish = async (): Promise<void> => {
    if (!text.trim() && files.length === 0) {
      useUiStore.getState().pushError('E_INVALID_INPUT');
      return;
    }
    setPublishing(true);
    try {
      const post = await container.feed.publish({ text, images: files });
      setText('');
      setFiles([]);
      if (fileRef.current) fileRef.current.value = '';
      markTaskDone('posted');
      useUiStore.getState().pushToast('success', '已发布到广场');
      const kind = files.length > 0 ? 'image' : 'post';
      void anchorKind(kind, post.manifestCid).then((ok) => {
        if (ok) useUiStore.getState().pushToast('success', `已上链锚定（${kind} 挖矿奖励已触发）`);
      });
    } catch (err) {
      useUiStore.getState().pushError(err instanceof MeshLinkError ? err.code : 'E_UNKNOWN');
    } finally {
      setPublishing(false);
    }
  };

  /** 创作（视频/语音/直播）：发布媒体帖 + 自动锚定对应 kind。 */
  const publishComposer = async (): Promise<void> => {
    if (!composer) return;
    if (composer !== 'live' && !mediaFile) {
      useUiStore.getState().pushError('E_INVALID_INPUT');
      return;
    }
    if (composer === 'live' && !composerText.trim()) {
      useUiStore.getState().pushError('E_INVALID_INPUT');
      return;
    }
    setPublishing(true);
    try {
      // 直播：以 🔴 前缀文本帖发布，锚定 kind=live 触发 live 挖矿奖励
      if (composer === 'live') {
        const livePost = await container.feed.publish({ text: `🔴 ${composerText}` });
        setComposer(null);
        setComposerText('');
        markTaskDone('posted');
        useUiStore.getState().pushToast('success', '直播帖已发布');
        void anchorKind('live', livePost.manifestCid).then((ok) => {
          if (ok) useUiStore.getState().pushToast('success', '已上链锚定（live 挖矿奖励已触发）');
        });
        return;
      }
      const post = await container.feed.publish({
        text: composerText,
        media: { file: mediaFile as File, kind: composer },
      });
      setComposer(null);
      setComposerText('');
      setMediaFile(null);
      if (mediaRef.current) mediaRef.current.value = '';
      markTaskDone('posted');
      useUiStore.getState().pushToast('success', '已发布媒体帖');
      const kind = composer === 'video' ? 'video' : 'audio';
      void anchorKind(kind, post.manifestCid).then((ok) => {
        if (ok) useUiStore.getState().pushToast('success', `已上链锚定（${kind} 挖矿奖励已触发）`);
      });
    } catch (err) {
      useUiStore.getState().pushError(err instanceof MeshLinkError ? err.code : 'E_UNKNOWN');
    } finally {
      setPublishing(false);
    }
  };

  /** 搜索：关键词哈希上链（search 挖矿）+ 本地过滤列表。 */
  const doSearch = async (): Promise<void> => {
    const q = query.trim();
    if (!q) return;
    const addr = container.identity.address;
    if (addr) {
      void container.chain
        .search({ from: addr as `0x${string}`, termHash: sha256Hex(q), signature: container.identity.signObject({ term: q, ts: Date.now() }) })
        .catch(() => undefined);
    }
    useUiStore.getState().pushToast('success', `搜索「${q}」（已上链记录）`);
  };

  return (
    <div className="space-y-4">
      <section className="card p-4">
        <div className="flex gap-2">
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') void doSearch();
            }}
            placeholder="搜索内容或作者（搜索即挖矿）"
          />
          <Button size="sm" onClick={() => void doSearch()}>
            搜索
          </Button>
        </div>
      </section>

      <section className="card p-4">
        <Textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="分享此刻…（支持配图，最多 6 张）"
        />
        <div className="mt-3 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              multiple
              className="hidden"
              onChange={(e) => pickFiles(e.target.files)}
            />
            <Button variant="ghost" size="sm" onClick={() => fileRef.current?.click()}>
              选择图片
            </Button>
            <span className="text-[11px] text-dim">
              {files.length}/{CONTENT_MAX_IMAGES_PER_POST} 张
            </span>
          </div>
          <Button size="sm" loading={publishing} onClick={() => void publish()}>
            发布
          </Button>
        </div>
        {files.length > 0 ? (
          <ul className="mt-2 space-y-1">
            {files.map((f) => (
              <li key={`${f.name}-${f.size}`} className="truncate text-[11px] text-dim">
                {f.name}
              </li>
            ))}
          </ul>
        ) : null}
      </section>

      <section className="card p-2">
        <div className="flex flex-wrap gap-2">
          <Button variant="ghost" size="sm" onClick={() => { setComposer('video'); setComposerText(''); setMediaFile(null); }}>
            📹 发视频
          </Button>
          <Button variant="ghost" size="sm" onClick={() => { setComposer('audio'); setComposerText(''); setMediaFile(null); }}>
            🎙 发语音
          </Button>
          <Button variant="ghost" size="sm" onClick={() => { setComposer('live'); setComposerText(''); setMediaFile(null); }}>
            🔴 开直播
          </Button>
        </div>
        {composer ? (
          <div className="mt-2 space-y-2 border-t border-line pt-2">
            {composer === 'live' ? (
              <Textarea
                value={composerText}
                onChange={(e) => setComposerText(e.target.value)}
                placeholder="直播标题（live 帖上链触发 live 挖矿奖励）"
              />
            ) : (
              <>
                <input
                  ref={mediaRef}
                  type="file"
                  accept={composer === 'video' ? 'video/*' : 'audio/*'}
                  className="hidden"
                  onChange={(e) => setMediaFile(e.target.files?.[0] ?? null)}
                />
                <div className="flex items-center gap-2">
                  <Button variant="ghost" size="sm" onClick={() => mediaRef.current?.click()}>
                    {composer === 'video' ? '选择视频' : '选择音频'}
                  </Button>
                  {mediaFile ? <span className="truncate text-[11px] text-dim">{mediaFile.name}（{Math.round(mediaFile.size / 1024)} KB）</span> : null}
                </div>
                <Textarea
                  value={composerText}
                  onChange={(e) => setComposerText(e.target.value)}
                  placeholder={`${composer === 'video' ? '视频' : '语音'}描述（可选，发布后自动锚定 ${composer} 挖矿奖励）`}
                />
              </>
            )}
            <div className="flex gap-2">
              <Button size="sm" loading={publishing} onClick={() => void publishComposer()}>
                发布
              </Button>
              <Button variant="ghost" size="sm" onClick={() => setComposer(null)}>
                取消
              </Button>
            </div>
          </div>
        ) : null}
      </section>

      {filtered.length === 0 ? (
        <EmptyState title="还没有动态" description="发布你的第一条帖子吧" />
      ) : (
        <section className="space-y-3">
          {filtered.map((post) => (
            <PostCard key={post.id} post={post} onReport={setReportTarget} />
          ))}
        </section>
      )}

      <ReportDialog
        key={reportTarget ?? 'none'}
        open={reportTarget !== null}
        defaultTargetType="post"
        defaultTargetId={reportTarget ?? ''}
        onClose={() => setReportTarget(null)}
      />
    </div>
  );
}

export default FeedPage;
